/**
 * Row in the new `artifacts` table. Written by the AI Celery job after an
 * SSE stream completes; served by Nest via `/artifacts/*` CRUD endpoints.
 * `content` is a plain string (typically markdown) — replaces the JSON
 * `artifact_payload` shape from the retired `artifact_demo_links` table.
 *
 * `/artifacts` is homogeneous. Prototypes are listed from `/prototypes`
 * and merged into {@link LibraryItem} on the client.
 */

import type { Prototype } from './prototyper.types';

export interface Artifact {
  kind?: 'artifact';
  id: string;
  llm_interaction_id: string;
  artifact_type: string;
  content?: string | null;
  /**
   * Structured JSON payload for the typed artifacts (JTBD, persona, 5 whys,
   * HMW). Present on the new `format: "json"` artifacts; the card UI renders
   * from this. `content` remains the pre-rendered markdown fallback.
   */
  structured_content?: Record<string, unknown> | null;
  demo_url?: string | null;
  /**
   * File-backed artifact metadata (PDF and other non-markdown formats).
   * `file_format` is the real downloadable format; a non-markdown value means
   * the binary should be fetched from `/artifacts/:id/download` rather than
   * saving the markdown `content`.
   */
  file_format?: string | null;
  file_name?: string | null;
  media_type?: string | null;
  file_status?: string | null;
  createdAt?: string;
  updatedAt?: string | null;
  /**
   * Owning conversation, present on scope/startup list rows so the library
   * can open the artifact inside that chat. Absent on single-row reads.
   */
  conversationId?: string | null;
  /** Scope of that conversation. Needed when the list is startup-wide. */
  scopeId?: string | null;
  /**
   * Discussion Room of that conversation, when the artifact was produced in a
   * startup-level discussion (scopeId is null then). Lets the library open it in
   * the discussion chat instead of a scope chat. Null for scope artifacts.
   */
  discussionRoomId?: string | null;
}

/**
 * Prototype row in the client library list. Identity lives in
 * `chef_project_links` (`GET /prototypes`); `kind` is added when merging.
 */
export type PrototypeLibraryItem = Prototype & { kind: 'prototype' };

export type ArtifactLibraryItem = Artifact & { kind: 'artifact' };

export type LibraryItem = ArtifactLibraryItem | PrototypeLibraryItem;

export function isPrototypeLibraryItem(item: LibraryItem): item is PrototypeLibraryItem {
  return item.kind === 'prototype';
}

function createdAtMs(item: { createdAt?: string | null }): number {
  if (!item.createdAt) return 0;
  const t = Date.parse(item.createdAt);
  return Number.isFinite(t) ? t : 0;
}

export function mergeLibraryItems(artifacts: Artifact[], prototypes: Prototype[]): LibraryItem[] {
  const items: LibraryItem[] = [
    ...artifacts.map((artifact): ArtifactLibraryItem => ({ ...artifact, kind: 'artifact' })),
    ...prototypes.map((prototype): PrototypeLibraryItem => ({ ...prototype, kind: 'prototype' })),
  ];
  items.sort((a, b) => createdAtMs(b) - createdAtMs(a));
  return items;
}

/**
 * Legacy alias for the artifact_demo_links row shape. Kept so the old
 * publish / drawing flows still typecheck; new code should use
 * {@link Artifact}.
 */
export interface ArtifactDemoLink {
  id: string | number;
  llmInteractionId?: string;
  tmpLlmInteractionId?: string;
  questionnaireType: string;
  artifactPayload: Record<string, unknown>;
  demoUrl?: string | null;
  createdAt?: string;
  updatedAt?: string | null;
  slug?: string | null;
  header?: string | null;
}

export interface UpdateArtifactPayload {
  artifact_type?: string;
  content?: string;
  /** Edited structured JSON for a typed artifact (PUT updates it in place). */
  structured_content?: Record<string, unknown>;
  demo_url?: string;
}
